import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { getPickups, getDisposalSites, getWorkers, assignDailyRoute, optimizeFleetDailyRoute, getAssignedRoutes, deleteAssignedRoute } from "@/services/api";
import { Loader2, MapPin, Package, Trash2, Navigation, RefreshCw, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

const kochiPosition = [9.9312, 76.2673];

// Helper to create a custom marker icon
const createColoredIcon = (color) => {
  const svgTemplate = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="25px" height="41px">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="white" stroke-width="1"/>
    </svg>`;
  
  return L.divIcon({
    className: "custom-colored-icon",
    html: svgTemplate,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: iconShadow,
    shadowSize: [41, 41],
    shadowAnchor: [12, 41]
  });
};

const LIGHT_BLUE_ICON = createColoredIcon("#38bdf8"); // Requested
const ORANGE_ICON = createColoredIcon("#f97316");     // Assigned
const TEAL_ICON = createColoredIcon("#0a2c99");       // Reached
const GREEN_ICON = createColoredIcon("#07993d");      // Completed
const RED_ICON = createColoredIcon("#ef4444");        // Missed

const getPickupIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'requested': return LIGHT_BLUE_ICON;
    case 'assigned': return ORANGE_ICON;
    case 'reached': return TEAL_ICON;
    case 'completed': 
    case 'collected': return GREEN_ICON;
    case 'missed': return RED_ICON;
    default: return LIGHT_BLUE_ICON;
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'requested': return "#38bdf8";
    case 'assigned': return "#f97316";
    case 'reached': return "#0a2c99";
    case 'completed': 
    case 'collected': return "#07993d";
    case 'missed': return "#ef4444";
    default: return "#38bdf8";
  }
};

// Translate Google Route Optimization skip reason codes into human-readable text.
// The backend joins multiple codes with ", " and falls back to a generic string when
// the API doesn't return a specific reason.
const SKIP_REASON_LABELS = {
  'CANNOT_BE_PERFORMED_WITHIN_VEHICLE_DISTANCE_LIMIT': 'Too far from the route start to reach within the shift.',
  'CANNOT_BE_PERFORMED_WITHIN_VEHICLE_DURATION_LIMIT': 'Does not fit within the shift duration.',
  'CANNOT_BE_PERFORMED_WITHIN_VEHICLE_TRAVEL_DURATION_LIMIT': 'Too far to reach within the shift travel time.',
  'CANNOT_BE_PERFORMED_WITHIN_VEHICLE_TIME_WINDOWS': 'Does not fit within the shift time window.',
  'VEHICLE_NOT_ALLOWED': 'No vehicle is allowed to serve this pickup.',
  'VEHICLE_IGNORED': 'The assigned vehicle is ignored.',
  'SHIPMENT_IGNORED': 'This pickup is ignored.',
  'SKIPPED_IN_INJECTED_SOLUTION_CONSTRAINT': 'Skipped by the injected solution.',
  'VEHICLE_ROUTE_IS_FULLY_SEQUENCE_CONSTRAINED': 'Vehicle route is fully constrained.',
  'ZERO_PENALTY_COST': 'Pickup has a zero penalty cost.'
};

const getSkipReasonText = (reason) => {
  if (!reason || reason === 'Skipped to avoid penalty') {
    return 'Could not be scheduled within the available shift time. Try increasing the number of routes (vehicles).';
  }
  return reason.split(',').map(code => {
    const trimmed = code.trim();
    return SKIP_REASON_LABELS[trimmed] || trimmed;
  }).join(', ');
};

const createNumberedIcon = (number, color) => {
  return L.divIcon({
    className: "custom-numbered-icon",
    html: `<div style="background-color: ${color}; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); z-index: 1000;">${number}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
};

const GARBAGE_ICON = L.divIcon({
  className: "custom-garbage-icon",
  html: `<div style="background-color: #295427; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">
           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
             <path d="M3 6h18"></path>
             <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
             <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
             <line x1="10" y1="11" x2="10" y2="17"></line>
             <line x1="14" y1="11" x2="14" y2="17"></line>
           </svg>
         </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

// Function to decode Google Maps polyline string into array of [lat, lng] coordinates
const decodePolyline = (str) => {
  let index = 0, lat = 0, lng = 0, coordinates = [];
  const factor = 1e5;

  while (index < str.length) {
    let b, shift = 0, result = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += dlng;

    coordinates.push([lat / factor, lng / factor]);
  }

  return coordinates;
};

const ChangeMapView = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 13, {
        duration: 1.5
      });
    }
  }, [center, map]);
  return null;
};

const RouteDesign = () => {
  const [viewMode, setViewMode] = useState('visual'); // 'visual' | 'route'
  const [todayPickups, setTodayPickups] = useState([]);
  const [disposalSites, setDisposalSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [workers, setWorkers] = useState([]);
  const [selectedStartDisposalSiteId, setSelectedStartDisposalSiteId] = useState("");
  const [endPoint, setEndPoint] = useState(null);
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  const [routeCount, setRouteCount] = useState(1);
  
  const [isAssignRouteDialogOpen, setIsAssignRouteDialogOpen] = useState(false);
  const [routeAssignments, setRouteAssignments] = useState({});

  // Manual Route State
  const [manualRoutePickups, setManualRoutePickups] = useState([]);
  const [isManualAssignDialogOpen, setIsManualAssignDialogOpen] = useState(false);
  const [manualRouteAssignment, setManualRouteAssignment] = useState({ captainId: "", date: new Date().toISOString().split('T')[0] });
  
  const [skippedPickups, setSkippedPickups] = useState([]);
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [assignedRoutes, setAssignedRoutes] = useState([]);
  const [selectedAgentRoute, setSelectedAgentRoute] = useState('all');
  const [isDeletingRoute, setIsDeletingRoute] = useState(false);

  const [isAssigningManual, setIsAssigningManual] = useState(false);
  const [assigningRouteIndex, setAssigningRouteIndex] = useState(null);

  const mapRef = useRef(null);

  const handlePrevDate = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleNextDate = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pickupsData, sitesData, workersData, assignedRoutesData] = await Promise.all([
          getPickups(500, null, selectedDate),
          getDisposalSites(),
          getWorkers(),
          getAssignedRoutes(selectedDate)
        ]);
        
        if (pickupsData && pickupsData.success) {
            setTodayPickups(pickupsData.pickups || []);
        }

        if (sitesData && sitesData.success) {
            setDisposalSites(sitesData.sites || []);
        }

        if (workersData && workersData.success) {
            setWorkers(workersData.workers || []);
        }

        if (assignedRoutesData && assignedRoutesData.success) {
            setAssignedRoutes(assignedRoutesData.routes || []);
        }

      } catch (err) {
        console.error("Error fetching data:", err);
        setError("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const validTodayPickups = todayPickups.filter(p => {
    if (!p.location?.lat || !p.location?.lng) return false;
    if (p.createdByAdmin !== true) return false;
    if (viewMode === 'route' || viewMode === 'manual') {
      return p.status === 'requested';
    }
    return true; // visual mode shows all
  });

  const pickupsWithoutLocation = todayPickups.filter(p => !p.location?.lat || !p.location?.lng);
  const pickupsWithoutLocationCount = pickupsWithoutLocation.length;

  const requestedCount = todayPickups.filter(p => p.status?.toLowerCase() === 'requested').length;
  const assignedCount = todayPickups.filter(p => p.status?.toLowerCase() === 'assigned').length;
  const reachedCount = todayPickups.filter(p => p.status?.toLowerCase() === 'reached').length;
  const collectedCount = todayPickups.filter(p => {
    const s = p.status?.toLowerCase();
    return s === 'completed' || s === 'collected';
  }).length;
  const missedCount = todayPickups.filter(p => p.status?.toLowerCase() === 'missed').length;

  const [isOptimizing, setIsOptimizing] = useState(false);

  const handleOptimizeRoute = async () => {
    if (!selectedStartDisposalSiteId) {
      toast.error("Please select a starting Disposal Site from the dropdown.");
      return;
    }

    const startSite = disposalSites.find(s => s.id === selectedStartDisposalSiteId || s._id === selectedStartDisposalSiteId);
    if (!startSite || !startSite.location?.lat) {
      toast.error("Selected Disposal Site is invalid or has no location.");
      return;
    }

    if (validTodayPickups.length === 0) {
      toast.error("No pending admin pickups to route today.");
      return;
    }

    const startPoint = {
      id: startSite.id || startSite._id,
      lat: startSite.location.lat,
      lng: startSite.location.lng,
      name: startSite.disposal_name || "Disposal Site",
      disposal_name: startSite.disposal_name
    };

    try {
      setIsOptimizing(true);
      
      const payload = {
        vehicleCount: parseInt(routeCount, 10) || 1,
        // The backend filters pickups by scheduled_date (YYYY-MM-DD) — send the
        // date currently selected in the UI so the optimize call runs against the
        // same pickups shown on screen. Without this the backend queries Firestore
        // with `scheduled_date == undefined`, which throws and fails the request.
        scheduled_date: selectedDate,
        startLocation: {
          lat: startPoint.lat,
          lng: startPoint.lng
        },
        endLocation: endPoint ? {
          lat: endPoint.lat,
          lng: endPoint.lng
        } : {
          lat: startPoint.lat,
          lng: startPoint.lng
        }
      };

      const res = await optimizeFleetDailyRoute(payload);

      if (res.success && res.data && res.data.routes) {
        const colors = ["#3b82f6", "#ef4444", "#eab308", "#a855f7", "#22c55e", "#ec4899", "#f97316"];
        
        const newRoutes = res.data.routes.map((r, idx) => {
          // Decode polyline
          let displayPath = [];
          if (r.polyline) {
             let fullPath = decodePolyline(r.polyline);
             
             // Truncate the polyline at the last actual pickup to hide the final leg to the end location
             if (r.visits && r.visits.length > 0) {
                 const lastPickup = r.visits[r.visits.length - 1].location;
                 let minLastDist = Infinity;
                 let lastIndex = fullPath.length - 1;
                 
                 for (let i = fullPath.length - 1; i >= 0; i--) {
                   const pt = fullPath[i];
                   const dLat = pt[0] - lastPickup.lat;
                   const dLng = pt[1] - lastPickup.lng;
                   const distSq = dLat*dLat + dLng*dLng;
                   if (distSq < minLastDist) {
                     minLastDist = distSq;
                     lastIndex = i;
                   }
                 }
                 displayPath = fullPath.slice(0, lastIndex + 1);
             } else {
                 displayPath = fullPath;
             }
          }

          // Reconstruct stops format for the UI (Start -> Visits -> End)
          const stops = [];
          stops.push({ ...startPoint, name: startPoint.name, disposal_name: startPoint.disposal_name });
          
          if (r.visits) {
            r.visits.forEach((v) => {
               const pickupData = todayPickups.find(p => (p._id || p.id) === v.pickupId);
               stops.push({
                  id: v.pickupId,
                  _id: v.pickupId,
                  lat: v.location.lat,
                  lng: v.location.lng,
                  name: pickupData ? (pickupData.user_name || pickupData.name) : "Pickup",
                  ...pickupData,
                  estimatedTime: v.estimatedTime
               });
            });
          }

          const finalEnd = endPoint || startPoint;
          stops.push({ ...finalEnd, name: finalEnd.name || finalEnd.disposal_name || "Return to Start", disposal_name: finalEnd.disposal_name });

          return {
             id: r.id,
             color: colors[idx % colors.length],
             path: displayPath,
             stops: stops,
             rawPolyline: r.polyline,
             metrics: {
                travelDistanceMeters: r.totalDistanceMeters,
                travelDuration: r.totalEstimatedTime
             }
          };
       });
        
        setOptimizedRoutes(newRoutes);
        
        if (res.data.skippedPickups) {
           setSkippedPickups(res.data.skippedPickups);
        } else {
           setSkippedPickups([]);
        }

        // Initialize assignments
        const initialAssignments = {};
        newRoutes.forEach((_, idx) => {
          initialAssignments[idx] = {
            captainId: "",
            date: selectedDate
          };
        });
        setRouteAssignments(initialAssignments);
        
        toast.success(`Optimized ${newRoutes.length} route(s) successfully!`);
      } else {
        toast.error("Failed to optimize route from backend.");
      }
    } catch (error) {
      console.error("Optimization error:", error);
      toast.error("Failed to optimize route.");
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleClearRoute = () => {
    setOptimizedRoutes([]);
    setSelectedStartDisposalSiteId("");
    setEndPoint(null);
    setSkippedPickups([]);
  };

  const handleClearManualRoute = () => {
    setManualRoutePickups([]);
    if (mapRef.current) {
      mapRef.current.closePopup();
    }
  };

  const handleDeleteAssignedRoute = async () => {
    if (selectedAgentRoute === 'all' || !selectedAgentRoute) {
      return toast.error("Please select a specific route to delete.");
    }

    try {
      setIsDeletingRoute(true);
      const res = await deleteAssignedRoute(selectedAgentRoute);
      if (res.success) {
        toast.success("Assigned route deleted successfully!");
        setSelectedAgentRoute('all');
        await fetchData(); // Refresh routes and pickups
      }
    } catch (error) {
      console.error("Delete assigned route error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete assigned route.");
    } finally {
      setIsDeletingRoute(false);
    }
  };

  const toggleManualRoutePickup = (pickup) => {
    setManualRoutePickups(prev => {
      const exists = prev.find(p => (p._id || p.id) === (pickup._id || pickup.id));
      if (exists) {
        return prev.filter(p => (p._id || p.id) !== (pickup._id || pickup.id));
      } else {
        return [...prev, pickup];
      }
    });
    if (mapRef.current) {
      mapRef.current.closePopup();
    }
  };

  const handleAssignManualRoute = async () => {
    if (!manualRouteAssignment.captainId) {
      return toast.error("Please select a captain for the manual route.");
    }
    
    try {
      setIsAssigningManual(true);
      const ordered_pickups = manualRoutePickups.map((p, i) => ({
        pickupId: p._id || p.id,
        sequence: i + 1,
        lat: p.location?.lat,
        lng: p.location?.lng
      }));
      
      if (ordered_pickups.length === 0) {
        return toast.error("No pickups in the manual route to assign.");
      }

      const payload = {
        captainId: manualRouteAssignment.captainId,
        date: manualRouteAssignment.date,
        ordered_pickups,
        polyline: "" // Empty polyline for manual routes
      };
      
      const res = await assignDailyRoute(payload);
      if (res.success) {
        toast.success("Manual route assigned successfully!");
        setIsManualAssignDialogOpen(false);
        handleClearManualRoute();

        const pickupsData = await getPickups(500, null, selectedDate);
        if (pickupsData && pickupsData.success) {
          setTodayPickups(pickupsData.pickups || []);
        }
      }
    } catch (error) {
      console.error("Assign manual route error:", error);
      toast.error(error.response?.data?.detail || "Failed to assign manual route.");
    } finally {
      setIsAssigningManual(false);
    }
  };

  const handleAssignSingleRoute = async (routeIndex, route) => {
    const assignment = routeAssignments[routeIndex];
    if (!assignment.captainId) {
      return toast.error(`Please select a captain for Route ${routeIndex + 1}`);
    }
    
    try {
      setAssigningRouteIndex(routeIndex);
      // Exclude disposal sites
      const pickupStops = route.stops.filter(s => (s._id || s.id) && !s.disposal_name && s.name !== "Return to Start");
      
      const ordered_pickups = pickupStops.map((s, i) => ({
        pickupId: s._id || s.id,
        sequence: i + 1
      }));
      
      if (ordered_pickups.length === 0) {
        return toast.error("No valid pickups in the route to assign.");
      }

      const payload = {
        captainId: assignment.captainId,
        date: assignment.date,
        ordered_pickups,
        polyline: route.rawPolyline // Send raw string
      };
      
      const res = await assignDailyRoute(payload);
      if (res.success) {
        toast.success(`Route ${routeIndex + 1} assigned successfully!`);
        
        const remainingRoutes = optimizedRoutes.filter((_, idx) => idx !== routeIndex);
        setOptimizedRoutes(remainingRoutes);
        
        if (remainingRoutes.length === 0) {
            setIsAssignRouteDialogOpen(false);
            handleClearRoute();
        }

        const pickupsData = await getPickups(500, null, selectedDate);
        if (pickupsData && pickupsData.success) {
          setTodayPickups(pickupsData.pickups || []);
        }
      }
    } catch (error) {
      console.error("Assign route error:", error);
      toast.error(error.response?.data?.detail || `Failed to assign Route ${routeIndex + 1}`);
    } finally {
      setAssigningRouteIndex(null);
    }
  };

  const isEndPoint = (id) => endPoint && endPoint.id === id;

  if (loading) {
    return (
      <div className="flex h-full min-h-[600px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground font-medium">Loading map data...</p>
        </div>
      </div>
    );
  }

  const seenPosPickups = {};

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <div className="flex flex-col">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1 border border-border">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handlePrevDate}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Input 
                  type="date" 
                  value={selectedDate} 
                  onChange={(e) => setSelectedDate(e.target.value)} 
                  className="h-8 border-none bg-transparent w-[130px] focus-visible:ring-0 px-1"
                />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleNextDate}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4 border border-border rounded-lg p-1.5 px-3 bg-muted/20">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input 
                type="checkbox" 
                className="rounded border-gray-300 text-primary focus:ring-primary"
                checked={viewMode === 'visual'} 
                onChange={() => setViewMode('visual')} 
              />
              Visual Mode
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input 
                type="checkbox" 
                className="rounded border-gray-300 text-primary focus:ring-primary"
                checked={viewMode === 'route'} 
                onChange={() => setViewMode('route')} 
              />
              Route Mode
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input 
                type="checkbox" 
                className="rounded border-gray-300 text-primary focus:ring-primary"
                checked={viewMode === 'manual'} 
                onChange={() => setViewMode('manual')} 
              />
              Manual Routing
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input 
                type="checkbox" 
                className="rounded border-gray-300 text-primary focus:ring-primary"
                checked={viewMode === 'agent'} 
                onChange={() => setViewMode('agent')} 
              />
              Show Agent Route
            </label>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
              {viewMode === 'agent' && (
                 <div className="flex items-center gap-2 bg-muted/50 p-1.5 rounded-lg border border-border">
                    <Select value={selectedAgentRoute} onValueChange={setSelectedAgentRoute}>
                      <SelectTrigger className="w-[200px] h-8 text-xs bg-white">
                        <SelectValue placeholder="Select Route" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Routes</SelectItem>
                        {assignedRoutes.map((route, idx) => {
                          const captainName = workers.find(w => (w._id || w.id) === route.agent_id)?.name || route.agent_id;
                          return (
                            <SelectItem key={route._id || route.id} value={route._id || route.id}>
                              Route {idx + 1} ({captainName})
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedAgentRoute !== 'all' && (
                      <div className="flex items-center border-l border-border pl-2 ml-1">
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          className="h-8 text-xs whitespace-nowrap"
                          onClick={handleDeleteAssignedRoute}
                          disabled={isDeletingRoute}
                        >
                          {isDeletingRoute ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Deleting...</> : <><Trash2 className="w-3 h-3 mr-1" /> Delete Route</>}
                        </Button>
                      </div>
                    )}
                 </div>
              )}
              {viewMode === 'route' && (
                <div className="flex items-center gap-2 bg-muted/50 p-1.5 rounded-lg border border-border">
                  <div className="flex items-center gap-2">
                    <Select value={selectedStartDisposalSiteId} onValueChange={setSelectedStartDisposalSiteId}>
                    <SelectTrigger className="w-[180px] h-8 text-xs bg-white">
                      <SelectValue placeholder="Start Disposal Site" />
                    </SelectTrigger>
                    <SelectContent>
                      {disposalSites.map(site => (
                        <SelectItem key={site.id || site._id} value={site.id || site._id}>{site.disposal_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 border-l border-border pl-2">
                  <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Routes:</label>
                  <Input 
                    type="number" 
                    min="1" 
                    max="10" 
                    className="w-14 h-8 text-xs bg-white" 
                    value={routeCount} 
                    onChange={(e) => setRouteCount(parseInt(e.target.value) || 1)} 
                  />
                </div>
                <div className="flex items-center gap-2 border-l border-border pl-2">
                  <div className="flex flex-col text-[10px] text-muted-foreground mr-1 min-w-[80px]">
                    <span className="truncate">End: <span className="font-semibold text-foreground">{endPoint ? (endPoint.name || "Selected") : "Loop to Start"}</span></span>
                  </div>
                  <Button onClick={handleOptimizeRoute} disabled={isOptimizing} size="sm" className="h-8 text-xs whitespace-nowrap">
                    {isOptimizing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> ...</> : "Optimize"}
                  </Button>
                </div>
                
                {optimizedRoutes.length > 0 && (
                  <div className="flex items-center gap-2 border-l border-border pl-2">
                    <Button size="sm" onClick={() => setIsAssignRouteDialogOpen(true)} className="h-8 text-xs whitespace-nowrap bg-green-600 hover:bg-green-700 text-white">
                      Assign ({optimizedRoutes.length})
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleClearRoute} className="h-8 w-8 p-0">
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

      <Card className="flex-grow border-border overflow-hidden shadow-md mt-2">
        <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <CardDescription className="flex items-center gap-3 text-[11px] xl:text-xs whitespace-nowrap overflow-x-auto no-scrollbar pb-1">
              <span className="flex items-center gap-1 shrink-0"><span className="w-3 h-3 rounded-full bg-[#38bdf8]"></span> Requested ({requestedCount})</span>
              <span className="flex items-center gap-1 shrink-0"><span className="w-3 h-3 rounded-full bg-[#f97316]"></span> Assigned ({assignedCount})</span>
              <span className="flex items-center gap-1 shrink-0"><span className="w-3 h-3 rounded-full bg-[#0a2c99]"></span> Reached ({reachedCount})</span>
              <span className="flex items-center gap-1 shrink-0"><span className="w-3 h-3 rounded-full bg-[#22c55e]"></span> Collected ({collectedCount})</span>
              <span className="flex items-center gap-1 shrink-0"><span className="w-3 h-3 rounded-full bg-[#ef4444]"></span> Missed ({missedCount})</span>
            </CardDescription>
          </div>
          
          <div className="flex-none flex justify-center shrink-0">
            {pickupsWithoutLocationCount > 0 ? (
              <HoverCard>
                <HoverCardTrigger className="cursor-pointer">
                  <Badge variant="outline" className="text-amber-600 border-amber-600 bg-amber-50">
                    {pickupsWithoutLocationCount} Pickup{pickupsWithoutLocationCount !== 1 && 's'} missing exact location
                  </Badge>
                </HoverCardTrigger>
                <HoverCardContent side="bottom" align="center" className="w-64 p-3 bg-white z-[9999] shadow-lg border border-border">
                  <h4 className="font-semibold text-sm mb-2 border-b pb-1">Customers Missing Location</h4>
                  <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                    {pickupsWithoutLocation.map((p, idx) => (
                      <div key={p.id || p._id || idx} className="flex flex-col gap-0.5 border-b border-border/50 pb-2 last:border-0 last:pb-0">
                        <span className="text-sm font-medium">{p.user_name || p.name || 'Unknown User'}</span>
                        <span className="text-[10px] text-muted-foreground truncate">{p.address || p.location?.address || 'No address provided'}</span>
                      </div>
                    ))}
                  </div>
                </HoverCardContent>
              </HoverCard>
            ) : (
              <span className="text-xs text-muted-foreground">All pickups have locations</span>
            )}
          </div>

          <div className="flex-none flex justify-end shrink-0">
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-8">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 h-[calc(100%-53px)]">
          <div className="h-full w-full z-0 relative">
            <MapContainer
              ref={mapRef}
              center={kochiPosition}
              zoom={11}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {viewMode !== 'agent' && validTodayPickups.map((p) => {
                const manualIndex = manualRoutePickups.findIndex(mp => (mp._id || mp.id) === (p._id || p.id));
                const isManualSelected = viewMode === 'manual' && manualIndex !== -1;
                const markerIcon = isManualSelected 
                  ? createNumberedIcon(manualIndex + 1, "#3b82f6") 
                  : getPickupIcon(p.status);

                let lat = p.location.lat;
                let lng = p.location.lng;
                const posKey = `${lat.toFixed(5)}-${lng.toFixed(5)}`;
                if (seenPosPickups[posKey]) {
                  lat += 0.0002 * seenPosPickups[posKey];
                  lng += 0.0002 * seenPosPickups[posKey];
                  seenPosPickups[posKey]++;
                } else {
                  seenPosPickups[posKey] = 1;
                }

                return (
                <Marker 
                  key={p._id || p.id} 
                  position={[lat, lng]} 
                  icon={markerIcon}
                  zIndexOffset={isManualSelected ? 1000 : 0}
                >
                  <Popup>
                    <div className="p-1 min-w-[150px]">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="w-4 h-4 text-primary" />
                        <h3 className="font-bold text-sm truncate">{p.user_name || "User"}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        📞 {p.user_phone || p.phone || p.contact || "No phone"}
                      </p>
                      <p className="text-xs text-muted-foreground">{p.location?.address || p.address}</p>
                      <div className="mt-2 border-t pt-2 border-border/50">
                          <p className="text-xs mb-1">Status: <span className="font-semibold uppercase">{p.status?.toLowerCase() === 'completed' ? 'Collected' : p.status}</span></p>
                          <p className="text-xs">Type: <span className="font-medium text-muted-foreground">{p.pickup_type === 'recurring' ? `Recurring (${p.frequency})` : 'One-time'}</span></p>
                      </div>
                      {viewMode === 'route' && (
                        <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-border/50">
                          <Button 
                            variant={isEndPoint(p._id || p.id) ? "default" : "outline"} 
                            size="sm" 
                            className="h-6 text-[10px] w-full"
                            onClick={() => {
                              if (isEndPoint(p._id || p.id)) {
                                setEndPoint(null); // toggle off
                              } else {
                                setEndPoint({ id: p._id || p.id, lat: p.location.lat, lng: p.location.lng, name: p.user_name || "Pickup", ...p });
                              }
                            }}
                          >
                            {isEndPoint(p._id || p.id) ? "Optional End Point Selected" : "Set as Optional End"}
                          </Button>
                        </div>
                      )}
                      {viewMode === 'manual' && (
                        <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-border/50">
                          <Button 
                            variant={manualRoutePickups.find(mp => (mp._id || mp.id) === (p._id || p.id)) ? "destructive" : "default"} 
                            size="sm" 
                            className="h-6 text-[10px] w-full"
                            onClick={() => toggleManualRoutePickup(p)}
                          >
                            {manualRoutePickups.find(mp => (mp._id || mp.id) === (p._id || p.id)) ? "Remove from Route" : "Add to Manual Route"}
                          </Button>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-2 pt-1">
                        <MapPin className="w-3 h-3" />
                        <span>{p.location.lat.toFixed(4)}, {p.location.lng.toFixed(4)}</span>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              )})}

              {disposalSites.filter(s => s.location?.lat && s.location?.lng).map((site) => (
                <Marker
                  key={site.id || site._id}
                  position={[site.location.lat, site.location.lng]}
                  icon={GARBAGE_ICON}
                >
                  <Popup>
                    <div className="p-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Trash2 className="w-4 h-4 text-blue-500" />
                        <p className="font-bold text-sm">{site.disposal_name || "Disposal Site"}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">{site.location?.formattedAddress}</p>
                      <p className="text-xs mt-2">
                        Status: <span className={`font-semibold uppercase ${site.status === 'open' ? 'text-green-600' : 'text-red-600'}`}>{site.status}</span>
                      </p>
                      {viewMode !== 'visual' && (
                        <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-border/50">
                          <Button 
                            variant={isEndPoint(site.id || site._id) ? "default" : "outline"} 
                            size="sm" 
                            className="h-6 text-[10px] w-full"
                            onClick={() => {
                              if (isEndPoint(site.id || site._id)) {
                                setEndPoint(null);
                              } else {
                                setEndPoint({ id: site.id || site._id, lat: site.location.lat, lng: site.location.lng, name: site.disposal_name || "Disposal Site", ...site });
                              }
                            }}
                          >
                            {isEndPoint(site.id || site._id) ? "Optional End Point Selected" : "Set as Optional End"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {viewMode === 'manual' && manualRoutePickups.length > 1 && (
                <Polyline 
                  positions={manualRoutePickups.map(p => [p.location.lat, p.location.lng])}
                  pathOptions={{ color: '#3b82f6', weight: 4, dashArray: '5, 10' }}
                />
              )}

              {viewMode === 'agent' && assignedRoutes
                .filter(r => selectedAgentRoute === 'all' || (r._id || r.id) === selectedAgentRoute)
                .map((route, routeIdx) => {
                  const colors = ["#3b82f6", "#ef4444", "#eab308", "#a855f7", "#22c55e", "#ec4899", "#f97316"];
                  const color = colors[routeIdx % colors.length];
                  
                  let path = [];
                  if (route.polyline) {
                    try {
                      path = decodePolyline(route.polyline);
                    } catch (e) {
                      console.error("Failed to decode polyline", e);
                    }
                  }

                  const mappedPickups = (route.ordered_pickups || []).map((stop, sIdx) => {
                    const pickupDetails = todayPickups.find(p => (p._id || p.id) === stop.pickupId);
                    return {
                      ...stop,
                      lat: stop.lat || pickupDetails?.location?.lat,
                      lng: stop.lng || pickupDetails?.location?.lng,
                      pickupDetails
                    };
                  }).filter(p => p.lat && p.lng);

                  let displayPath = [];
                  if (path.length > 0 && mappedPickups.length >= 2) {
                    const firstStop = mappedPickups[0];
                    const lastStop = mappedPickups[mappedPickups.length - 1];
                    
                    let minFirstDist = Infinity;
                    let firstIndex = 0;
                    
                    let minLastDist = Infinity;
                    let lastIndex = path.length - 1;
                    
                    for (let i = 0; i < path.length; i++) {
                      const pt = path[i];
                      const dLat1 = pt[0] - firstStop.lat;
                      const dLng1 = pt[1] - firstStop.lng;
                      const dist1 = dLat1*dLat1 + dLng1*dLng1;
                      
                      if (dist1 < minFirstDist) {
                        minFirstDist = dist1;
                        firstIndex = i;
                      }
                      
                      const dLatLast = pt[0] - lastStop.lat;
                      const dLngLast = pt[1] - lastStop.lng;
                      const distLast = dLatLast*dLatLast + dLngLast*dLngLast;
                      
                      if (distLast < minLastDist) {
                        minLastDist = distLast;
                        lastIndex = i;
                      }
                    }
                    
                    if (firstIndex <= lastIndex) {
                      displayPath = path.slice(firstIndex, lastIndex + 1);
                    } else {
                      // Just in case they are reversed
                      displayPath = path.slice(lastIndex, firstIndex + 1).reverse();
                    }
                  }

                  return (
                    <React.Fragment key={route._id || route.id}>
                      {displayPath.length > 0 ? (
                        <Polyline 
                          positions={displayPath}
                          pathOptions={{ color, weight: 4, opacity: 0.7 }}
                        />
                      ) : mappedPickups.length > 1 ? (
                        <Polyline 
                          positions={mappedPickups.map(p => [p.lat, p.lng])}
                          pathOptions={{ color, weight: 4, dashArray: '5, 10' }}
                        />
                      ) : null}
                      
                      {mappedPickups.map((pos, idx) => {
                        const statusColor = getStatusColor(pos.pickupDetails?.status);
                        
                        let lat = pos.lat;
                        let lng = pos.lng;
                        const posKey = `${lat.toFixed(5)}-${lng.toFixed(5)}`;
                        if (seenPosPickups[posKey]) {
                          lat += 0.0002 * seenPosPickups[posKey];
                          lng += 0.0002 * seenPosPickups[posKey];
                          seenPosPickups[posKey]++;
                        } else {
                          seenPosPickups[posKey] = 1;
                        }

                        return (
                          <Marker 
                            key={`${route._id || route.id}-agent-point-${idx}`}
                            position={[lat, lng]}
                            icon={createNumberedIcon(idx + 1, statusColor)}
                            zIndexOffset={1000}
                          >
                            <Popup>
                              <div className="p-1">
                                <p className="font-bold text-sm" style={{ color: statusColor }}>Stop {idx + 1}</p>
                                <p className="text-xs font-semibold">{pos.pickupDetails?.user_name || "Unknown Pickup"}</p>
                                <p className="text-xs text-muted-foreground">{pos.pickupDetails?.location?.address || pos.pickupDetails?.address}</p>
                                <div className="mt-1 pt-1 border-t border-border/50">
                                  <p className="text-xs mb-1">Status: <span className="font-semibold uppercase">{pos.pickupDetails?.status?.toLowerCase() === 'completed' ? 'Collected' : (pos.pickupDetails?.status || "Unknown")}</span></p>
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </React.Fragment>
                  );
              })}

              {viewMode === 'route' && optimizedRoutes.map(route => {
                const seenPos = {}; // Track coordinates to prevent overlap hiding
                
                // Always hide the last stop (the End Point)
                const visibleStops = route.stops.slice(0, -1);

                return (
                  <React.Fragment key={route.id}>
                    <Polyline 
                      positions={route.path}
                      pathOptions={{ color: route.color, weight: 4, dashArray: '5, 10' }}
                    />
                    {visibleStops.map((pos, idx) => {
                      // Skip drawing the Start Point marker to avoid overlap with Disposal Site icon
                      if (idx === 0) return null;

                      let lat = pos.lat;
                      let lng = pos.lng;

                      // Check for exact coordinate overlaps to prevent markers from hiding each other
                      const posKey = `${lat.toFixed(5)}-${lng.toFixed(5)}`;
                      if (seenPos[posKey]) {
                        // Offset by a tiny visible amount (approx 10-20 meters)
                        lat += 0.0002 * seenPos[posKey];
                        lng += 0.0002 * seenPos[posKey];
                        seenPos[posKey]++;
                      } else {
                        seenPos[posKey] = 1;
                      }
                      
                      const isStart = idx === 0;
                      // Since we sliced off the fake end, we only show End Point if one was manually set
                      const isEnd = endPoint && idx === visibleStops.length - 1;
                      const label = isStart ? "Start Point" : isEnd ? "End Point" : `Stop ${idx}`;

                      return (
                        <Marker 
                          key={`${route.id}-point-${idx}`}
                          position={[lat, lng]}
                          icon={createNumberedIcon(idx, route.color)}
                          zIndexOffset={1000}
                        >
                          <Popup>
                            <div className="p-1">
                              <p className="font-bold text-sm" style={{ color: route.color }}>{label}</p>
                              <p className="text-xs">{pos.name || pos.user_name || pos.disposal_name}</p>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              
              {validTodayPickups.length > 0 && <ChangeMapView center={[validTodayPickups[0].location.lat, validTodayPickups[0].location.lng]} />}
            </MapContainer>
            
            {/* Skipped Pickups Sidebar Panel */}
            {viewMode === 'route' && skippedPickups.length > 0 && (
              <div className="absolute top-4 right-4 z-[1000] w-80 max-h-[80%] flex flex-col bg-white rounded-lg shadow-xl border border-border overflow-hidden">
                <div className="bg-destructive/90 p-3 text-destructive-foreground flex justify-between items-center">
                  <h3 className="font-semibold text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Skipped Pickups</h3>
                  <Badge variant="outline" className="text-xs bg-white text-destructive border-white">{skippedPickups.length}</Badge>
                </div>
                
                <div className="flex-1 overflow-y-auto p-3 bg-muted/10 space-y-2">
                  {skippedPickups.map((sp, idx) => {
                    const pickupData = todayPickups.find(p => (p._id || p.id) === sp.pickupId);
                    return (
                      <div key={sp.pickupId || idx} className="flex flex-col p-2 bg-white rounded border shadow-sm text-sm">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
                          <span className="font-semibold text-xs truncate">{pickupData?.user_name || pickupData?.name || "Unknown"}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground truncate mt-1">{pickupData?.location?.address || pickupData?.address}</span>
                        <span className="text-[10px] text-destructive mt-1 font-medium bg-destructive/10 p-1 rounded">Reason: {getSkipReasonText(sp.reason)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Manual Route Sidebar Panel */}
            {viewMode === 'manual' && (
              <div className="absolute top-4 right-4 z-[1000] w-80 max-h-[80%] flex flex-col bg-white rounded-lg shadow-xl border border-border overflow-hidden">
                <div className="bg-primary p-3 text-primary-foreground flex justify-between items-center">
                  <h3 className="font-semibold text-sm flex items-center gap-2"><Navigation className="w-4 h-4" /> Manual Route</h3>
                  <Badge variant="secondary" className="text-xs">{manualRoutePickups.length} Stops</Badge>
                </div>
                
                <div className="flex-1 overflow-y-auto p-3 bg-muted/10 space-y-2">
                  {manualRoutePickups.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground text-xs">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-20" />
                      Click on pickup pins and select "Add to Manual Route" to build your sequence.
                    </div>
                  ) : (
                    manualRoutePickups.map((p, idx) => (
                      <div key={p._id || p.id} className="flex items-center justify-between p-2 bg-white rounded border shadow-sm text-sm">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <Badge className="w-6 h-6 rounded-full flex items-center justify-center p-0 shrink-0 bg-primary text-primary-foreground">
                            {idx + 1}
                          </Badge>
                          <div className="flex flex-col truncate">
                            <span className="font-semibold text-xs truncate">{p.user_name || p.name || "Unknown"}</span>
                            <span className="text-[10px] text-muted-foreground truncate">{p.location?.address || p.address}</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => toggleManualRoutePickup(p)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
                
                {manualRoutePickups.length > 0 && (
                  <div className="p-3 bg-white border-t flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={handleClearManualRoute}>Clear</Button>
                    <Button size="sm" className="flex-1 text-xs h-8" onClick={() => setIsManualAssignDialogOpen(true)}>Assign Route</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {validTodayPickups.length === 0 && !error && !loading && (
        <div className="bg-muted/30 border border-dashed border-border rounded-lg p-8 text-center mt-4">
          <p className="text-muted-foreground">No pickups scheduled for today with valid locations.</p>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-center mt-4">
          {error}
        </div>
      )}

      {/* Manual Route Assign Dialog */}
      <Dialog open={isManualAssignDialogOpen} onOpenChange={setIsManualAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Navigation className="w-5 h-5 text-primary" /> Assign Manual Route</DialogTitle>
            <DialogDescription>Assign this manually ordered route to a captain.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Captain</label>
              <Select 
                value={manualRouteAssignment.captainId} 
                onValueChange={(val) => setManualRouteAssignment(prev => ({ ...prev, captainId: val }))}
              >
                <SelectTrigger><SelectValue placeholder="Select Captain" /></SelectTrigger>
                <SelectContent>
                  {workers.map((w) => <SelectItem key={w._id || w.id} value={w._id || w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Date</label>
              <Input 
                type="date" 
                value={manualRouteAssignment.date} 
                onChange={(e) => setManualRouteAssignment(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div className="mt-4">
              <p className="text-xs text-muted-foreground mb-2">Total Pickups: <strong>{manualRoutePickups.length}</strong></p>
            </div>
          </div>
          <DialogFooter className="gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setIsManualAssignDialogOpen(false)} disabled={isAssigningManual}>Cancel</Button>
            <Button onClick={handleAssignManualRoute} disabled={isAssigningManual}>
              {isAssigningManual ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning...</> : "Confirm Assignment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Route Dialog */}
      <Dialog open={isAssignRouteDialogOpen} onOpenChange={setIsAssignRouteDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Navigation className="w-5 h-5 text-primary" /> Assign Optimized Routes</DialogTitle>
            <DialogDescription>Assign the generated routes to your captains.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {optimizedRoutes.map((route, routeIndex) => {
              const assignment = routeAssignments[routeIndex] || { captainId: "", date: "" };
              const pickupCount = route.stops.filter(s => s._id || (s.id && !s.disposal_name)).length;

              return (
                <Card key={route.id} className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: route.color }}></div>
                      Route {routeIndex + 1} ({pickupCount} Pickups)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Select Captain</label>
                        <Select 
                          value={assignment.captainId} 
                          onValueChange={(val) => setRouteAssignments(prev => ({ ...prev, [routeIndex]: { ...prev[routeIndex], captainId: val } }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Select Captain" /></SelectTrigger>
                          <SelectContent>
                            {workers.map((w) => <SelectItem key={w._id || w.id} value={w._id || w.id}>{w.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Date</label>
                        <Input 
                          type="date" 
                          value={assignment.date} 
                          onChange={(e) => setRouteAssignments(prev => ({ ...prev, [routeIndex]: { ...prev[routeIndex], date: e.target.value } }))}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2 pt-2">
                      <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Ordered Pickups Only</label>
                      <div className="max-h-[200px] overflow-y-auto border rounded-md divide-y">
                        {route.stops
                          .map((stop, idx) => ({ ...stop, originalIndex: idx })) // Keep track of the original stop sequence number
                          .filter(stop => (stop._id || stop.id) && !stop.disposal_name && stop.name !== "Return to Start") // Exclude Disposal Sites and start/end points
                          .map((stop, index) => {
                            // index is purely the visual index in this filtered list, sequence is 1-based index in the payload
                            return (
                              <div key={stop._id || stop.id || index} className="flex items-center justify-between p-2 hover:bg-muted/50 text-sm">
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline" className="w-6 h-6 rounded-full flex items-center justify-center p-0 shrink-0">
                                    {index + 1}
                                  </Badge>
                                  <div className="flex flex-col">
                                    <span className="font-semibold">
                                      {stop.user_name || stop.name || "Unknown Pickup"}
                                    </span>
                                    <span className="text-xs text-muted-foreground truncate max-w-[200px]">{stop.address || stop.location?.address}</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="text-[10px] text-muted-foreground block">Stop {stop.originalIndex} • Pickup ID</span>
                                  <span className="font-mono text-xs">{stop._id || stop.id}</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    
                    <div className="pt-2 flex justify-end">
                      <Button 
                        size="sm" 
                        onClick={() => handleAssignSingleRoute(routeIndex, route)}
                        disabled={assigningRouteIndex === routeIndex}
                      >
                        {assigningRouteIndex === routeIndex ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning...</> : `Assign Route ${routeIndex + 1}`}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter className="gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setIsAssignRouteDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RouteDesign;
